import {
  ERROR_EXPIRED,
  ERROR_NO_USES_REMAIN,
  ERROR_NOT_FOUND,
} from '@/app/[locale]/store/_consts/discounts';
import type { DiscountContextValues } from '@/app/[locale]/store/_contexts/DiscountProvider';
import type { GetOneDiscountResult } from '@/queries/GetOneDiscount';
import type { SavePromotionResult } from '@/queries/SavePromotion';
import { getSupportedDiscountGenerations } from '@/utils/discountUtils';

interface ShowDiscountMessage extends Pick<
  DiscountContextValues,
  'urlDiscount' | 'urlDiscountIsSuccess'
> {
  gen3WizardConsumer: boolean;
  isAccessory: boolean;
  urlPromotionData?: SavePromotionResult | null | undefined;
}

interface GetValidDiscountStatus {
  hasValidGen3DiscountForGen3Wiz: boolean | null | undefined;
  hasValidGen4DiscountForGen4Wiz: boolean | null | undefined;
  hasValidAllRingsDiscount: boolean | null | undefined;
}

// _getIsNotGen4Discount takes a Discount and returns true if no gen4 ring is represented on it.
const _getIsNotGen4Discount = (
  discount: GetOneDiscountResult | null,
): boolean => {
  const supportedDiscountGenerations =
    getSupportedDiscountGenerations(discount);
  return (
    !supportedDiscountGenerations.has('gen4') &&
    !supportedDiscountGenerations.has('gen4-ceramic')
  );
};

// _getIsAllRingsDiscount takes a Discount and returns true if both gen4 and any previous generation rings are represented on it.
const _getIsAllRingsDiscount = (
  discount: GetOneDiscountResult | null,
): boolean => {
  const supportedDiscountGenerations =
    getSupportedDiscountGenerations(discount);

  // The set will contain more than 1 item.
  return supportedDiscountGenerations.size > 1;
};

const _getValidDiscountStatus = ({
  gen3WizardConsumer,
  urlDiscount,
  urlDiscountIsSuccess,
}: Omit<
  ShowDiscountMessage,
  'isAccessory' | 'urlPromotionData'
>): GetValidDiscountStatus => {
  const validDiscountStatus: GetValidDiscountStatus = {
    hasValidGen3DiscountForGen3Wiz: null,
    hasValidGen4DiscountForGen4Wiz: null,
    hasValidAllRingsDiscount: null,
  };
  if (!urlDiscount) return validDiscountStatus;
  const isNotGen4Discount = _getIsNotGen4Discount(urlDiscount);
  const isAllRingsDiscount = _getIsAllRingsDiscount(urlDiscount);
  const isSuccessWithPayload = urlDiscountIsSuccess && urlDiscount;

  // If the discount is valid for Gen3 wizard, show the discount message on Gen3 only
  validDiscountStatus.hasValidGen3DiscountForGen3Wiz =
    isSuccessWithPayload && gen3WizardConsumer && isNotGen4Discount;
  // If the discount is valid for Gen4 wizard, show the discount message on Gen4 only
  validDiscountStatus.hasValidGen4DiscountForGen4Wiz =
    isSuccessWithPayload && !gen3WizardConsumer && !isNotGen4Discount;
  // If the discount is valid for all rings, show the discount message on all rings
  validDiscountStatus.hasValidAllRingsDiscount =
    isSuccessWithPayload && isAllRingsDiscount;

  return validDiscountStatus;
};

const showDiscountMessage = ({
  isAccessory,
  gen3WizardConsumer,
  urlDiscount,
  urlDiscountIsSuccess,
}: ShowDiscountMessage): boolean => {
  const {
    hasValidGen3DiscountForGen3Wiz,
    hasValidGen4DiscountForGen4Wiz,
    hasValidAllRingsDiscount,
  } = _getValidDiscountStatus({
    gen3WizardConsumer,
    urlDiscount,
    urlDiscountIsSuccess,
  });
  const urlDiscountInvalidReaon = urlDiscount?.content?.discount?.invalidReason;
  // If no discount is found, show the discount message
  const noDiscountFound = urlDiscountInvalidReaon === ERROR_NOT_FOUND;
  // If the discount is expired, show the discount message
  const hasExpiredDiscount = urlDiscountInvalidReaon === ERROR_EXPIRED;
  // If the discount usage limit is reached, show the discount message
  const hasLimitReached = urlDiscountInvalidReaon === ERROR_NO_USES_REMAIN;

  switch (true) {
    case isAccessory:
      return false;
    case hasValidGen3DiscountForGen3Wiz:
      return true;
    case hasValidGen4DiscountForGen4Wiz:
      return true;
    case hasValidAllRingsDiscount:
      return true;
    case noDiscountFound:
      return true;
    case hasExpiredDiscount:
      return true;
    case hasLimitReached:
      return true;
    default:
      return false;
  }
};

export default showDiscountMessage;
